import { useState, createContext, useContext, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./auth";
import { useLoader } from "./loader";
import { toast } from "react-hot-toast";

const CartContext = createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [auth] = useAuth();
  const [loading, setLoading] = useLoader(false);

  // ✅ Load cart from DB when user logs in
  useEffect(() => {
    if (auth?.token) {
      getCartFromDB();
    } else {
      setCart([]); // logout → clear cart
    }
  }, [auth?.token]);

  // 🔥 GET CART API
  const getCartFromDB = async () => {
    try {
      const { data } = await axios.get("/cart");

      if (data?.cart?.items) {
        const formattedCart = data.cart.items.map((item) => ({
          ...item.product,
          cartQuantity: item.quantity,
        }));
        setCart(formattedCart);
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ ADD TO CART (API)
  const addToCart = async (productId, silent = false) => {
    try {
      const { data } = await axios.post("/cart/add", {
        productId,
        quantity: 1,
      });
      if (!silent) toast.success("Added to cart");

      if (data.success) {
        await getCartFromDB();
      }
    } catch (err) {
      toast.error("Failed to add to cart");
      console.log(err);
    }
  };

  // ✅ UPDATE CART (inc / dec / remove)
  const updateCartItem = async (productId, action) => {
    try {
      const { data } = await axios.post("/cart/update", {
        productId,
        action,
      });

      if (data?.success) {
        const formattedCart = data.cart.items.map((item) => ({
          ...item.product,
          cartQuantity: item.quantity,
        }));
        setCart(formattedCart);
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ MOVE CART ITEM TO WISHLIST (atomic — removes from cart & adds to wishlist)
  const moveToWishlist = async (productId, onWishlistRefresh) => {
    try {
      const { data } = await axios.post("/cart/move-to-wishlist", { productId });

      if (data?.success) {
        // Refresh cart from response
        const formattedCart = (data.cart?.items || []).map((item) => ({
          ...item.product,
          cartQuantity: item.quantity,
        }));
        setCart(formattedCart);
        toast.success("Moved to Wishlist ❤️");

        // Optional: re-fetch wishlist in context
        if (onWishlistRefresh) await onWishlistRefresh();
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to move to wishlist");
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addToCart,
        updateCartItem,
        moveToWishlist,
        refreshCart: getCartFromDB,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

export { useCart, CartProvider };